// src/app/socketManager/DemoSocketManager.ts
import type { ISocketManager } from "../ISocketManager";
import type { socketEvents } from "../socketManager";

type MockResponder = (...args: any[]) => { event: string; payload: any } | void;

export class DemoSocketManager implements ISocketManager {
  public readonly namespace: string;
  private connected = false;
  private socketEvents: socketEvents = {};
  private listeners = new Map<string, Set<(payload: any) => void>>();
  private onceListeners = new Map<string, Set<(payload: any) => void>>();
  private enableLogging: boolean;

  constructor(
    namespace: string,
    socketEvents: socketEvents,
    enableLogging?: boolean,
  ) {
    this.namespace = namespace;
    this.socketEvents = socketEvents;
    this.enableLogging = enableLogging || false;
  }

  private mockResponders = new Map<string, MockResponder>();

  /** Registriert, was auf ein bestimmtes emit() simuliert zurückkommt */
  registerMockResponse(event: string, responder: MockResponder): void {
    this.mockResponders.set(event, responder);
  }

  emit(event: string, ...args: any[]): void {
    this.enableLogging &&
      console.debug(`[Demo:${this.namespace}] emit "${event}"`, args);

    const responder = this.mockResponders.get(event);
    if (!responder) return;

    // Künstliche Latenz, damit es sich nicht "instant" anfühlt
    setTimeout(() => {
      const result = responder(...args);
      if (result) this.simulateServerEvent(result.event, result.payload);
    }, 250);
  }

  // simulateServerEvent(...) bleibt wie besprochen

  async connect(): Promise<void> {
    // Kein Token-Check, keine echte Handshake-Latenz nötig — leichte
    // künstliche Verzögerung reicht, damit UI-Loading-States nicht ausflippen
    await new Promise((r) => setTimeout(r, 150));
    this.connected = true;
    this.enableLogging && console.debug(`[Demo:${this.namespace}] Connected`);
    this.socketEvents.onConnected?.();
  }

  async handleTokenRefresh(): Promise<void> {
    // no-op: es gibt in der Demo kein echtes Token, das ablaufen könnte
  }

  isConnected(): boolean {
    return this.connected;
  }

  ensureConnected(): void {}

  setEvents(events: socketEvents): void {
    this.socketEvents = events;
  }

  once<T = any>(event: string, callback: (payload: T) => void): void {
    if (!this.onceListeners.has(event))
      this.onceListeners.set(event, new Set());
    this.onceListeners.get(event)!.add(callback);
  }

  on<T = any>(event: string, callback: (payload: T) => void): void {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(callback);
  }

  off(event: string, callback?: (...args: any[]) => void): void {
    if (callback) {
      this.listeners.get(event)?.delete(callback);
      this.onceListeners.get(event)?.delete(callback);
    } else {
      this.listeners.delete(event);
      this.onceListeners.delete(event);
    }
  }

  disconnect(): void {
    this.connected = false;
    this.listeners.clear();
    this.onceListeners.clear();
  }

  /**
   * Nur für Demo-Zwecke: von außen (z.B. demoData.ts) aufrufen,
   * um simulierte Server-Events auf registrierte Listener zu feuern —
   * mit denselben Event-Namen, die euer echter Server auch nutzt.
   */
  simulateServerEvent<T = any>(event: string, payload: T): void {
    this.listeners.get(event)?.forEach((cb) => cb(payload));
    const once = this.onceListeners.get(event);
    if (once) {
      once.forEach((cb) => cb(payload));
      this.onceListeners.delete(event);
    }
  }
}
